import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../services/api';
import Layout from '../components/Layout';

const AGENT_NAMES: Record<string, string> = {
  factsagent: 'FactsAgent',
  topicalmap: 'Topical Map V1',
  topicalmapv2: 'Topical Map V2',
  cppages: 'CP Pages',
  content_writer: 'Blog Posts',
  contentframeworkv2: 'Content Framework V2',
  contentframework: 'Content Framework V1',
  cpcustom: 'CF Custom',
  cpimages: 'CP Images',
  briefcontent: 'Bulk Brief',
  liveupdate: 'WP Live Update',
  payloadagent: 'Payload Agent',
  imagegenerator: 'Image Generator',
  socialmedia: 'Social Media',
  gdriveeditor: 'Google Drive',
  articleupdate: 'Article Update',
  icpagent: 'ICP Agent',
  brandbrain: 'Brand Brain',
  llmvisibility: 'LLM Visibility',
  intentvolume: 'Intent Volume',
  serviceareapages: 'Service Area Pages',
  contentgen: 'Content Gen',
  bahaagents: 'Baha Agents',
  seotools: 'SEO Tools',
};

function agentName(st: string): string {
  if (!st) return 'Unknown';
  return AGENT_NAMES[st] || st;
}

function formatTokens(n: number): string {
  if (!n) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function formatCost(usd: number): string {
  if (!usd) return '$0.00';
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  return `$${usd.toFixed(2)}`;
}

type TabType = 'logs' | 'system' | 'api-calls';

export default function SystemMonitorPage() {
  const [tab, setTab] = useState<TabType>('logs');
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Logs state
  const [logsData, setLogsData] = useState<any>(null);
  const [logFilters, setLogFilters] = useState({ level: '', system_type: '', search: '', job_id: '', page: 1 });

  // System metrics state
  const [systemMetrics, setSystemMetrics] = useState<any>(null);
  const metricsIntervalRef = useRef<any>(null);

  // API calls state
  const [apiCalls, setApiCalls] = useState<any>(null);
  const [apiFilter, setApiFilter] = useState({ system_type: '', provider: '' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params: any = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      if (tab === 'logs') {
        const logParams: any = { ...params, page: logFilters.page, page_size: 50 };
        if (logFilters.level) logParams.level = logFilters.level;
        if (logFilters.system_type) logParams.system_type = logFilters.system_type;
        if (logFilters.search) logParams.search = logFilters.search;
        if (logFilters.job_id) logParams.job_id = logFilters.job_id;
        const res = await api.get('/useractivity/analytics/logs', { params: logParams });
        setLogsData(res.data);
      } else if (tab === 'system') {
        const res = await api.get('/useractivity/analytics/system-metrics');
        setSystemMetrics(res.data);
      } else if (tab === 'api-calls') {
        const apiParams: any = { ...params };
        if (apiFilter.system_type) apiParams.system_type = apiFilter.system_type;
        if (apiFilter.provider) apiParams.provider = apiFilter.provider;
        const res = await api.get('/useractivity/analytics/api-calls', { params: apiParams });
        setApiCalls(res.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load data');
    }
    setLoading(false);
  }, [tab, dateFrom, dateTo, logFilters, apiFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh system metrics every 10s
  useEffect(() => {
    if (tab === 'system') {
      metricsIntervalRef.current = setInterval(async () => {
        try {
          const res = await api.get('/useractivity/analytics/system-metrics');
          setSystemMetrics(res.data);
        } catch {}
      }, 10000);
    }
    return () => { if (metricsIntervalRef.current) clearInterval(metricsIntervalRef.current); };
  }, [tab]);

  const handleDeleteLogs = async (params: any) => {
    if (!confirm('Are you sure you want to delete these logs? This cannot be undone.')) return;
    try {
      const res = await api.delete('/useractivity/analytics/logs', { params });
      alert(res.data.message);
      fetchData();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to delete logs');
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">System Monitor</h1>
          <p className="text-gray-500 text-sm mt-1">Logs, system resources, and API call tracking</p>
        </div>

        {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}

        {/* Tabs + Date Filter */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            {([
              { key: 'logs' as TabType, label: 'Log Analyzer' },
              { key: 'system' as TabType, label: 'System Resources' },
              { key: 'api-calls' as TabType, label: 'API Calls & Cost' },
            ]).map(t => (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  tab === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                {t.label}
              </button>
            ))}
          </div>
          {tab !== 'system' && (
            <div className="flex items-center gap-2">
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              <span className="text-gray-400">to</span>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              <button onClick={fetchData} className="px-4 py-2 bg-brand-600 text-white rounded-lg text-sm hover:bg-brand-700">
                Refresh
              </button>
            </div>
          )}
          {tab === 'system' && (
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-400 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                Auto-refreshes every 10s
              </span>
              <button onClick={fetchData} className="px-4 py-2 bg-brand-600 text-white rounded-lg text-sm hover:bg-brand-700">
                Refresh Now
              </button>
            </div>
          )}
        </div>

        {loading && (
          <div className="flex items-center justify-center py-20">
            <span className="spinner mr-2"></span>
            <span className="text-gray-500">Loading...</span>
          </div>
        )}

        {/* ═══════════════ LOGS TAB ═══════════════ */}
        {!loading && tab === 'logs' && logsData && (
          <LogsView
            data={logsData}
            filters={logFilters}
            onFilterChange={(f) => setLogFilters({ ...logFilters, ...f, page: f.page ?? 1 })}
            onDelete={handleDeleteLogs}
          />
        )}

        {/* ═══════════════ SYSTEM TAB ═══════════════ */}
        {!loading && tab === 'system' && systemMetrics && (
          <SystemMetricsView data={systemMetrics} />
        )}

        {/* ═══════════════ API CALLS TAB ═══════════════ */}
        {!loading && tab === 'api-calls' && apiCalls && (
          <ApiCallsView
            data={apiCalls}
            filter={apiFilter}
            onFilterChange={(f) => setApiFilter({ ...apiFilter, ...f })}
            onRefresh={fetchData}
          />
        )}
      </div>
    </Layout>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════
   LOGS VIEW — Full in-depth log analyzer
   ═══════════════════════════════════════════════════════════════════════════ */

function LogsView({ data, filters, onFilterChange, onDelete }: {
  data: any;
  filters: any;
  onFilterChange: (f: any) => void;
  onDelete: (params: any) => void;
}) {
  const { total, page, total_pages, level_counts, system_counts, logs } = data;
  const [searchInput, setSearchInput] = useState(filters.search || '');
  const [jobIdInput, setJobIdInput] = useState(filters.job_id || '');
  const [expandedLog, setExpandedLog] = useState<number | null>(null);

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <MiniStat label="Total Logs" value={total.toLocaleString()} color="gray" />
        <MiniStat label="INFO" value={(level_counts?.INFO || 0).toLocaleString()} color="blue" />
        <MiniStat label="WARNING" value={(level_counts?.WARNING || 0).toLocaleString()} color="amber" />
        <MiniStat label="ERROR" value={(level_counts?.ERROR || 0).toLocaleString()} color="red" />
        <div className="rounded-xl border px-4 py-3 bg-red-50 border-red-200 text-red-700">
          <p className="text-xs opacity-70 mb-1">Cleanup Actions</p>
          <div className="flex flex-wrap gap-1">
            <button onClick={() => onDelete({ level: 'INFO' })}
              className="text-xs bg-red-100 hover:bg-red-200 px-2 py-0.5 rounded transition-colors">
              Delete INFO
            </button>
            <button onClick={() => {
              const d = new Date(); d.setDate(d.getDate() - 7);
              onDelete({ before_date: d.toISOString().split('T')[0] });
            }}
              className="text-xs bg-red-100 hover:bg-red-200 px-2 py-0.5 rounded transition-colors">
              Delete 7d+ old
            </button>
            <button onClick={() => {
              const d = new Date(); d.setDate(d.getDate() - 30);
              onDelete({ before_date: d.toISOString().split('T')[0] });
            }}
              className="text-xs bg-red-100 hover:bg-red-200 px-2 py-0.5 rounded transition-colors">
              Delete 30d+ old
            </button>
          </div>
        </div>
      </div>

      {/* Agent distribution */}
      {system_counts.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Logs by Agent</h3>
          <div className="flex flex-wrap gap-2">
            {system_counts.map((s: any) => {
              const isSelected = filters.system_type === s.system_type;
              return (
                <button key={s.system_type}
                  onClick={() => onFilterChange({ system_type: isSelected ? '' : s.system_type })}
                  className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                    isSelected
                      ? 'bg-brand-600 text-white border-brand-600'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-brand-300 hover:text-brand-700'
                  }`}>
                  {agentName(s.system_type)} <span className="font-bold ml-1">{s.count}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Level</label>
            <select value={filters.level} onChange={e => onFilterChange({ level: e.target.value })}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm min-w-[100px]">
              <option value="">All Levels</option>
              <option value="INFO">INFO</option>
              <option value="WARNING">WARNING</option>
              <option value="ERROR">ERROR</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Job #</label>
            <div className="flex gap-1">
              <input type="text" value={jobIdInput} onChange={e => setJobIdInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') onFilterChange({ job_id: jobIdInput }); }}
                placeholder="e.g. 42"
                className="w-24 px-3 py-1.5 border border-gray-300 rounded-lg text-sm" />
              <button onClick={() => onFilterChange({ job_id: jobIdInput })}
                className="px-2 py-1.5 bg-gray-100 border border-gray-300 rounded-lg text-sm hover:bg-gray-200">Go</button>
            </div>
          </div>
          <div className="flex-1 min-w-[250px]">
            <label className="text-xs text-gray-500 block mb-1">Search Message</label>
            <div className="flex gap-2">
              <input type="text" value={searchInput} onChange={e => setSearchInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') onFilterChange({ search: searchInput }); }}
                placeholder="Search log messages..."
                className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm" />
              <button onClick={() => onFilterChange({ search: searchInput })}
                className="px-3 py-1.5 bg-brand-600 text-white rounded-lg text-sm hover:bg-brand-700">Search</button>
              {(filters.search || filters.level || filters.system_type || filters.job_id) && (
                <button onClick={() => {
                  setSearchInput('');
                  setJobIdInput('');
                  onFilterChange({ search: '', level: '', system_type: '', job_id: '', page: 1 });
                }}
                  className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-sm hover:bg-gray-200">Clear</button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="max-h-[650px] overflow-y-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 w-44">Time</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 w-16">Job #</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 w-32">Agent</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 w-20">Level</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">Message</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {logs.map((log: any) => (
                <>
                  <tr key={log.id}
                    className={`hover:bg-gray-50 cursor-pointer ${
                      log.level === 'ERROR' ? 'bg-red-50/40' : log.level === 'WARNING' ? 'bg-amber-50/30' : ''
                    }`}
                    onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}>
                    <td className="px-4 py-2 text-xs text-gray-500 whitespace-nowrap">
                      {log.created_at ? new Date(log.created_at).toLocaleString() : '-'}
                    </td>
                    <td className="px-4 py-2 text-xs font-mono text-gray-600">#{log.job_id}</td>
                    <td className="px-4 py-2 text-xs text-gray-600">{agentName(log.system_type)}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-flex px-2 py-0.5 text-xs rounded-full font-medium ${
                        log.level === 'ERROR' ? 'bg-red-100 text-red-700' :
                        log.level === 'WARNING' ? 'bg-amber-100 text-amber-700' :
                        'bg-blue-100 text-blue-700'}`}>
                        {log.level}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      <p className="truncate max-w-md" title={log.message}>{log.message}</p>
                    </td>
                    <td className="px-4 py-2 text-gray-400">
                      <svg className={`w-4 h-4 transition-transform ${expandedLog === log.id ? 'rotate-180' : ''}`}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </td>
                  </tr>
                  {expandedLog === log.id && (
                    <tr key={`${log.id}-expanded`}>
                      <td colSpan={6} className="px-4 py-3 bg-gray-50">
                        <div className="text-sm text-gray-700 whitespace-pre-wrap font-mono bg-white rounded-lg p-4 border border-gray-200 max-h-[300px] overflow-y-auto">
                          {log.message}
                        </div>
                        <div className="flex gap-4 mt-2 text-xs text-gray-400">
                          <span>Log ID: {log.id}</span>
                          <span>Job: #{log.job_id}</span>
                          <span>Agent: {agentName(log.system_type)}</span>
                          <span>Level: {log.level}</span>
                          <button onClick={(e) => { e.stopPropagation(); onDelete({ job_id: log.job_id }); }}
                            className="text-red-500 hover:text-red-700 ml-auto">
                            Delete all logs for Job #{log.job_id}
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
              {logs.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-gray-400">No logs match your filters</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {total_pages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between bg-white">
            <span className="text-xs text-gray-500">
              Page {page} of {total_pages} ({total.toLocaleString()} total logs)
            </span>
            <div className="flex gap-2">
              <button disabled={page <= 1} onClick={() => onFilterChange({ page: page - 1 })}
                className="px-3 py-1 text-sm border rounded-lg disabled:opacity-40 hover:bg-gray-50">Prev</button>
              {/* Page number buttons */}
              {Array.from({ length: Math.min(5, total_pages) }, (_, i) => {
                const p = Math.max(1, Math.min(page - 2, total_pages - 4)) + i;
                if (p > total_pages) return null;
                return (
                  <button key={p} onClick={() => onFilterChange({ page: p })}
                    className={`px-3 py-1 text-sm border rounded-lg ${p === page ? 'bg-brand-600 text-white border-brand-600' : 'hover:bg-gray-50'}`}>
                    {p}
                  </button>
                );
              })}
              <button disabled={page >= total_pages} onClick={() => onFilterChange({ page: page + 1 })}
                className="px-3 py-1 text-sm border rounded-lg disabled:opacity-40 hover:bg-gray-50">Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════
   SYSTEM METRICS VIEW — CPU, Memory, Disk, Processes
   ═══════════════════════════════════════════════════════════════════════════ */

function SystemMetricsView({ data }: { data: any }) {
  if (data.error) {
    return (
      <div className="bg-red-50 text-red-700 rounded-xl p-6 border border-red-200">
        <p className="font-medium">psutil not installed</p>
        <p className="text-sm mt-1">Run <code className="bg-red-100 px-1.5 py-0.5 rounded">pip install psutil</code> on the server to enable system monitoring.</p>
      </div>
    );
  }

  const { cpu, memory, disk, process: proc, top_processes } = data;

  return (
    <div className="space-y-6">
      {/* Main gauges */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <GaugeCard label="CPU Usage" value={cpu.percent} unit="%" subtitle={`${cpu.count} cores${cpu.freq_mhz ? ` @ ${cpu.freq_mhz}MHz` : ''}`} color="blue" />
        <GaugeCard label="Memory Usage" value={memory.percent} unit="%" subtitle={`${memory.used_gb}GB / ${memory.total_gb}GB`} color="purple" />
        <GaugeCard label="Disk Usage" value={disk.percent} unit="%" subtitle={`${disk.used_gb}GB / ${disk.total_gb}GB (${disk.free_gb}GB free)`} color="amber" />
        <GaugeCard label="BNSystem Process" value={proc.cpu_percent} unit="% CPU" subtitle={`PID ${proc.pid} | ${proc.memory_mb}MB RAM`} color="brand" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Memory breakdown */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-base font-semibold text-gray-900 mb-4">Memory Details</h3>
          <div className="space-y-4">
            <ResourceBar label="Used Memory" value={memory.used_gb} max={memory.total_gb} unit="GB" color="purple" />
            <ResourceBar label="Available Memory" value={memory.available_gb} max={memory.total_gb} unit="GB" color="green" />
            <div className="pt-3 border-t border-gray-100 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Total RAM</span>
                <span className="font-medium">{memory.total_gb}GB</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">BNSystem Process</span>
                <span className="font-medium text-brand-600">{proc.memory_mb}MB ({((proc.memory_mb / (memory.total_gb * 1024)) * 100).toFixed(1)}%)</span>
              </div>
            </div>
          </div>
        </div>

        {/* Disk breakdown */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-base font-semibold text-gray-900 mb-4">Disk Details</h3>
          <div className="space-y-4">
            <ResourceBar label="Used Space" value={disk.used_gb} max={disk.total_gb} unit="GB" color="amber" />
            <ResourceBar label="Free Space" value={disk.free_gb} max={disk.total_gb} unit="GB" color="green" />
            <div className="pt-3 border-t border-gray-100">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Total Disk</span>
                <span className="font-medium">{disk.total_gb}GB</span>
              </div>
              {disk.percent > 85 && (
                <div className="mt-2 p-2 bg-red-50 rounded-lg text-xs text-red-700">
                  Disk usage is above 85% - consider cleaning up old files
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Top processes */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900">Top Processes by Memory</h3>
        </div>
        <div className="max-h-[400px] overflow-y-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">PID</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">Process</th>
                <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500">Memory %</th>
                <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500">CPU %</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 w-48">Memory Bar</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {top_processes.map((p: any, i: number) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-xs font-mono text-gray-400">{p.pid}</td>
                  <td className="px-4 py-2 text-sm text-gray-700 font-medium">{p.name}</td>
                  <td className="px-4 py-2 text-right text-sm text-gray-600">{p.memory_percent}%</td>
                  <td className="px-4 py-2 text-right text-sm text-gray-600">{p.cpu_percent}%</td>
                  <td className="px-4 py-2">
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-purple-500 rounded-full" style={{ width: `${Math.min(p.memory_percent * 5, 100)}%` }} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════
   API CALLS VIEW — Costs, tokens, per-agent breakdown
   ═══════════════════════════════════════════════════════════════════════════ */

function ApiCallsView({ data, filter, onFilterChange, onRefresh }: {
  data: any;
  filter: { system_type: string; provider: string };
  onFilterChange: (f: any) => void;
  onRefresh: () => void;
}) {
  const { totals, by_agent, by_provider, by_model, daily_trend, by_user, recent } = data;

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <MiniStat label="Total Calls" value={totals.total_calls.toLocaleString()} color="brand" />
        <MiniStat label="Input Tokens" value={formatTokens(totals.total_input_tokens)} color="blue" />
        <MiniStat label="Output Tokens" value={formatTokens(totals.total_output_tokens)} color="indigo" />
        <MiniStat label="Total Tokens" value={formatTokens(totals.total_tokens)} color="purple" />
        <MiniStat label="Total Cost" value={formatCost(totals.total_cost_usd)} color="green" />
        <MiniStat label="Avg Latency" value={`${totals.avg_duration_ms}ms`} color="amber" />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Agent</label>
            <select value={filter.system_type} onChange={e => onFilterChange({ system_type: e.target.value })}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm min-w-[150px]">
              <option value="">All Agents</option>
              {by_agent.map((a: any) => (
                <option key={a.system_type} value={a.system_type}>{agentName(a.system_type)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Provider</label>
            <select value={filter.provider} onChange={e => onFilterChange({ provider: e.target.value })}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm min-w-[130px]">
              <option value="">All Providers</option>
              {by_provider.map((p: any) => (
                <option key={p.provider} value={p.provider}>{p.provider}</option>
              ))}
            </select>
          </div>
          {(filter.system_type || filter.provider) && (
            <button onClick={() => { onFilterChange({ system_type: '', provider: '' }); }}
              className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-sm hover:bg-gray-200">Clear Filters</button>
          )}
        </div>
      </div>

      {/* Daily trend */}
      {daily_trend.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily API Usage & Cost</h3>
          <div className="flex items-end gap-px h-36 overflow-x-auto">
            {daily_trend.map((day: any) => {
              const maxTokens = Math.max(...daily_trend.map((d: any) => d.tokens), 1);
              const height = Math.max((day.tokens / maxTokens) * 100, 2);
              return (
                <div key={day.date} className="flex-1 min-w-[8px] flex flex-col items-center group relative">
                  <div className="w-full bg-indigo-500 rounded-t hover:bg-indigo-600 transition-colors cursor-pointer"
                    style={{ height: `${height}%` }}
                    title={`${day.date}: ${day.calls} calls, ${formatTokens(day.tokens)} tokens, ${formatCost(day.cost)}`} />
                  <div className="absolute -top-12 left-1/2 -translate-x-1/2 hidden group-hover:block bg-gray-900 text-white text-xs px-2 py-1.5 rounded whitespace-nowrap z-10">
                    <p>{day.date}</p>
                    <p>{day.calls} calls | {formatTokens(day.tokens)} tokens | {formatCost(day.cost)}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-2 text-xs text-gray-400">
            <span>{daily_trend[0]?.date}</span>
            <span>{daily_trend[daily_trend.length - 1]?.date}</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By Agent — most important */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-base font-semibold text-gray-900">API Usage by Agent</h3>
            <p className="text-xs text-gray-400 mt-0.5">Which agents consume the most API calls and tokens</p>
          </div>
          <div className="max-h-[400px] overflow-y-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Agent</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Calls</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Tokens</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Cost</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Avg ms</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Errors</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {by_agent.map((a: any) => (
                  <tr key={a.system_type} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5 text-sm font-medium text-gray-700">{agentName(a.system_type)}</td>
                    <td className="px-4 py-2.5 text-right text-sm text-gray-700">{a.calls}</td>
                    <td className="px-4 py-2.5 text-right text-sm text-gray-700">{formatTokens(a.tokens)}</td>
                    <td className="px-4 py-2.5 text-right text-sm font-medium text-green-700">{formatCost(a.cost)}</td>
                    <td className="px-4 py-2.5 text-right text-sm text-gray-500">{a.avg_ms}</td>
                    <td className="px-4 py-2.5 text-right text-sm">{a.errors ? <span className="text-red-600 font-medium">{a.errors}</span> : <span className="text-gray-300">0</span>}</td>
                  </tr>
                ))}
                {by_agent.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No API call data yet. Integrate track_api_call() into services.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* By Provider + By Model */}
        <div className="space-y-6">
          {/* By Provider */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-4">By Provider</h3>
            {by_provider.length > 0 ? (
              <div className="space-y-3">
                {by_provider.map((p: any) => {
                  const maxTokens = by_provider[0]?.tokens || 1;
                  const pct = (p.tokens / maxTokens) * 100;
                  return (
                    <div key={p.provider}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium text-gray-700 capitalize">{p.provider}</span>
                        <span className="text-gray-500">{p.calls} calls | {formatTokens(p.tokens)} | {formatCost(p.cost)}</span>
                      </div>
                      <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-400 text-sm">No data yet</p>
            )}
          </div>

          {/* By Model */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-4">By Model</h3>
            {by_model.length > 0 ? (
              <div className="space-y-2.5">
                {by_model.map((m: any) => (
                  <div key={m.model} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                    <div>
                      <p className="text-sm font-medium text-gray-700">{m.model}</p>
                      <p className="text-xs text-gray-400">{m.provider}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">{m.calls} calls | {formatTokens(m.tokens)}</p>
                      <p className="text-sm font-semibold text-green-700">{formatCost(m.cost)}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-sm">No data yet</p>
            )}
          </div>
        </div>
      </div>

      {/* By User */}
      {by_user.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-base font-semibold text-gray-900">API Usage by User</h3>
          </div>
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">User</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Calls</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Tokens</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Cost</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {by_user.map((u: any, i: number) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5">
                    <p className="text-sm font-medium text-gray-700">{u.name}</p>
                    <p className="text-xs text-gray-400">{u.email}</p>
                  </td>
                  <td className="px-4 py-2.5 text-right text-sm text-gray-700">{u.calls}</td>
                  <td className="px-4 py-2.5 text-right text-sm text-gray-700">{formatTokens(u.tokens)}</td>
                  <td className="px-4 py-2.5 text-right text-sm font-medium text-green-700">{formatCost(u.cost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Recent API Calls */}
      {recent.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-base font-semibold text-gray-900">Recent API Calls</h3>
          </div>
          <div className="max-h-[400px] overflow-y-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Time</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Agent</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Project</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Model</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Input</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Output</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Cost</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Latency</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {recent.map((r: any) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-3 py-1.5 text-xs text-gray-500 whitespace-nowrap">
                      {r.created_at ? new Date(r.created_at).toLocaleString() : '-'}
                    </td>
                    <td className="px-3 py-1.5 text-xs text-gray-700">
                      {agentName(r.system_type)}
                      {r.agent_name && <span className="text-gray-400 ml-1">/ {r.agent_name}</span>}
                    </td>
                    <td className="px-3 py-1.5 text-xs text-gray-600 max-w-[120px] truncate" title={r.project_name || '-'}>{r.project_name || '-'}</td>
                    <td className="px-3 py-1.5 text-xs text-gray-600 font-mono">{r.model?.split('-').slice(0, 2).join('-')}</td>
                    <td className="px-3 py-1.5 text-right text-xs text-gray-600">{formatTokens(r.input_tokens)}</td>
                    <td className="px-3 py-1.5 text-right text-xs text-gray-600">{formatTokens(r.output_tokens)}</td>
                    <td className="px-3 py-1.5 text-right text-xs font-medium text-green-700">{formatCost(r.cost_usd)}</td>
                    <td className="px-3 py-1.5 text-right text-xs text-gray-500">{r.duration_ms ? `${r.duration_ms}ms` : '-'}</td>
                    <td className="px-3 py-1.5">
                      <span className={`inline-flex px-1.5 py-0.5 text-[10px] rounded-full ${
                        r.status === 'success' ? 'bg-green-100 text-green-700' :
                        r.status === 'error' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-600'}`}>
                        {r.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


/* ═══════════════ SHARED COMPONENTS ═══════════════ */

function GaugeCard({ label, value, unit, subtitle, color }: {
  label: string; value: number; unit: string; subtitle: string; color: string;
}) {
  const colorMap: Record<string, { ring: string; text: string }> = {
    blue: { ring: 'text-blue-500', text: 'text-blue-700' },
    purple: { ring: 'text-purple-500', text: 'text-purple-700' },
    amber: { ring: 'text-amber-500', text: 'text-amber-700' },
    brand: { ring: 'text-brand-500', text: 'text-brand-700' },
  };
  const c = colorMap[color] || colorMap.blue;
  const pct = Math.min(value, 100);
  const circumference = 2 * Math.PI * 40;
  const offset = circumference - (pct / 100) * circumference;

  return (
    <div className="rounded-xl border border-gray-200 p-5 bg-white">
      <div className="flex items-center gap-4">
        <div className="relative w-20 h-20 flex-shrink-0">
          <svg className="w-20 h-20 -rotate-90" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="40" fill="none" stroke="#e5e7eb" strokeWidth="8" />
            <circle cx="50" cy="50" r="40" fill="none" className={c.ring} strokeWidth="8"
              strokeDasharray={circumference} strokeDashoffset={offset}
              strokeLinecap="round" />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className={`text-sm font-bold ${c.text}`}>{value}{unit === '%' ? '%' : ''}</span>
          </div>
        </div>
        <div>
          <p className="text-sm font-semibold text-gray-900">{label}</p>
          {unit !== '%' && <p className="text-lg font-bold text-gray-900">{value} {unit}</p>}
          <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
        </div>
      </div>
    </div>
  );
}

function ResourceBar({ label, value, max, unit, color }: {
  label: string; value: number; max: number; unit: string; color: string;
}) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  const colorMap: Record<string, string> = { purple: 'bg-purple-500', green: 'bg-green-500', amber: 'bg-amber-500', blue: 'bg-blue-500' };
  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-gray-600">{label}</span>
        <span className="font-medium">{value}{unit} <span className="text-gray-400">/ {max}{unit}</span></span>
      </div>
      <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full ${colorMap[color] || 'bg-blue-500'} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function MiniStat({ label, value, color }: { label: string; value: string | number; color: string }) {
  const colorMap: Record<string, string> = {
    brand: 'bg-brand-50 border-brand-200 text-brand-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    green: 'bg-green-50 border-green-200 text-green-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    gray: 'bg-gray-50 border-gray-200 text-gray-700',
  };
  return (
    <div className={`rounded-xl border px-4 py-3 ${colorMap[color] || colorMap.gray}`}>
      <p className="text-xs opacity-70 mb-0.5">{label}</p>
      <p className="text-lg font-bold">{value}</p>
    </div>
  );
}
